import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

import {
  getWatchAreas, createWatchArea, updateWatchArea, deleteWatchArea,
} from "../../../api/garageSales";

const TEAL = "#55c5d0";
const inputClass = "px-3 py-2 rounded-lg border border-gray-300 text-sm";

const auDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

/**
 * The suburbs the scheduled sync pulls from Garage Sale Finder.
 *
 * Areas overlap freely — sales are deduped on the source's own listing id, so an
 * extra area costs one page fetch, never a duplicate row. Admin only.
 */
const WatchAreasModal = ({ onClose }) => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ suburb: "", postcode: "", radius_km: 10 });

  const load = () => {
    setLoading(true);
    getWatchAreas()
      .then((res) => setAreas(res.data || []))
      .catch(() => toast.error("Could not load the watch areas."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.suburb.trim()) return toast.error("Give the suburb to watch.");
    setSaving(true);
    try {
      await createWatchArea({ ...form, state: "VIC", radius_km: Number(form.radius_km) || 10 });
      setForm({ suburb: "", postcode: "", radius_km: 10 });
      toast.success("Area added. It is picked up on the next sync.");
      load();
    } catch (err) {
      const data = err?.response?.data;
      const first = data && typeof data === "object" ? Object.values(data)[0] : null;
      toast.error(Array.isArray(first) ? first[0] : first || "Could not add that area.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (area) => {
    try {
      await updateWatchArea(area.id, { is_active: !area.is_active });
      load();
    } catch {
      toast.error("Could not change that area.");
    }
  };

  const remove = async (area) => {
    if (!window.confirm(`Stop watching ${area.suburb}?`)) return;
    try {
      await deleteWatchArea(area.id);
      load();
    } catch {
      toast.error("Could not remove that area.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-extrabold text-gray-900">Areas we watch</h2>
            <p className="text-[11px] text-gray-500">
              Suburbs the Garage Sale Finder sync pulls, a few times a day.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="px-5 py-4">
          <form onSubmit={add} className="flex flex-wrap items-end gap-2 mb-5">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-gray-400 mb-1">
                Suburb
              </label>
              <input
                value={form.suburb}
                onChange={(e) => setForm((f) => ({ ...f, suburb: e.target.value }))}
                className={`${inputClass} w-full`}
                placeholder="Ivanhoe"
              />
            </div>
            <div className="w-28">
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-gray-400 mb-1">
                Postcode
              </label>
              <input
                value={form.postcode}
                onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))}
                className={`${inputClass} w-full`}
                placeholder="3079"
              />
            </div>
            <div className="w-24">
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-gray-400 mb-1">
                Radius km
              </label>
              <input
                type="number" min="1" max="50"
                value={form.radius_km}
                onChange={(e) => setForm((f) => ({ ...f, radius_km: e.target.value }))}
                className={`${inputClass} w-full`}
              />
            </div>
            <button type="submit" disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-60"
                    style={{ background: TEAL }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
              Add
            </button>
          </form>

          {loading ? (
            <div className="py-10 flex items-center justify-center text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : areas.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No areas yet. Add the suburbs you prospect.
            </p>
          ) : (
            <div className="space-y-2">
              {areas.map((area) => (
                <div key={area.id} className="border border-gray-200 rounded-lg px-3 py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900">
                      {area.suburb} {area.postcode} · {area.radius_km}km
                    </div>
                    <div className="text-[11px] text-gray-500">
                      Last synced {auDateTime(area.last_synced_at)} · {area.last_sync_count} sale(s)
                    </div>
                    {area.last_sync_error && (
                      <div className="text-[11px] text-red-500 flex items-center gap-1 mt-0.5">
                        <AlertTriangle size={11} /> {area.last_sync_error}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => toggle(area)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-extrabold ${
                      area.is_active ? "bg-[#e9f7ee] text-[#34b56a]" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {area.is_active ? "Active" : "Paused"}
                  </button>
                  <button onClick={() => remove(area)} className="p-1.5 rounded-lg hover:bg-red-50">
                    <Trash2 size={15} className="text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WatchAreasModal;
